import type {
  NormalizedRateLimitConfig,
  RateLimitDecision,
  RateLimitSnapshot,
} from '../../contracts/rateLimit';
import { CRITICAL_SIGNAL_TYPES } from '../../contracts/rateLimit';
import type { BehaviorSignalType } from '../../contracts/signals';

const CRITICAL_SET = new Set<BehaviorSignalType>(CRITICAL_SIGNAL_TYPES);

/**
 * Limitador interno por instância (Etapa 20). Sliding window com limpeza lazy
 * por timestamp — sem timers permanentes. Sinais críticos podem ignorar o limite
 * (criticalBypass) e nunca contam contra a janela.
 */
export class BehaviorRateLimiter {
  private readonly config: NormalizedRateLimitConfig;

  /** Timestamps dos sinais aceitos na janela deslizante corrente. */
  private windowTimestamps: number[] = [];

  private accepted = 0;

  private dropped = 0;

  private bypassed = 0;

  constructor(config: NormalizedRateLimitConfig) {
    this.config = config;
  }

  reset(): void {
    this.windowTimestamps = [];
    this.accepted = 0;
    this.dropped = 0;
    this.bypassed = 0;
  }

  /** Decide de forma determinística se um sinal deve seguir o pipeline. */
  evaluate(type: BehaviorSignalType, now: number): RateLimitDecision {
    if (!this.config.enabled) {
      this.accepted += 1;
      return 'accepted';
    }

    if (this.config.criticalBypass && CRITICAL_SET.has(type)) {
      this.bypassed += 1;
      return 'bypassed';
    }

    this.prune(now);

    if (this.windowTimestamps.length < this.config.maxSignalsPerWindow) {
      this.windowTimestamps.push(now);
      this.accepted += 1;
      return 'accepted';
    }

    this.dropped += 1;
    return 'dropped';
  }

  private prune(now: number): void {
    const cutoff = now - this.config.windowMs;
    // Timestamps são monotônicos crescentes: remove os expirados do início.
    let removeCount = 0;
    for (const ts of this.windowTimestamps) {
      if (ts <= cutoff) removeCount += 1;
      else break;
    }
    if (removeCount > 0) {
      this.windowTimestamps.splice(0, removeCount);
    }
  }

  getSnapshot(): RateLimitSnapshot {
    return {
      enabled: this.config.enabled,
      windowMs: this.config.windowMs,
      maxSignalsPerWindow: this.config.maxSignalsPerWindow,
      criticalBypass: this.config.criticalBypass,
      accepted: this.accepted,
      dropped: this.dropped,
      bypassed: this.bypassed,
      windowCount: this.windowTimestamps.length,
      windowStartedAt: this.windowTimestamps[0],
    };
  }
}
